import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Rocket, Clock, Target, TrendingUp } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function AutoApply() {
  return (
    <>
      <Helmet>
        <title>AI Smart Apply - Apply to 100+ Jobs Automatically | LoopBaan.ai</title>
        <meta name="description" content="Let AI apply to jobs for you 24/7. Automated job applications with custom resumes and cover letters for each position." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">PREMIUM FEATURE</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Apply to 100+ Jobs{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  While You Sleep
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                AI searches, tailors, and submits applications automatically. Set your preferences once, then watch the interviews roll in.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Smart Apply <Rocket className="h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline">View Pricing</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Clock,
                  title: "24/7 Automation",
                  description: "AI works around the clock searching for jobs and submitting applications while you focus on interview prep."
                },
                {
                  icon: Target,
                  title: "Smart Targeting",
                  description: "Set your preferences for role, salary, location, and company size. AI only applies to jobs that match."
                },
                {
                  icon: TrendingUp,
                  title: "Custom Applications",
                  description: "Each application gets a tailored resume and cover letter optimized for that specific job posting."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Features</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Search 1M+ jobs across all platforms",
                  "AI-tailored resume for each application",
                  "Custom cover letters for every job",
                  "Set daily application limits",
                  "Filter by salary, location, experience",
                  "Application tracking dashboard",
                  "Response rate analytics",
                  "Priority support"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-8">Success Stats</h2>
              <div className="grid gap-8 md:grid-cols-3">
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">50K+</div>
                  <div className="text-muted-foreground">Applications Submitted</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">85%</div>
                  <div className="text-muted-foreground">Response Rate</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">18 Days</div>
                  <div className="text-muted-foreground">Average Time to Offer</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stop Applying Manually
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let AI handle applications while you prepare for interviews. Start today.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Smart Apply <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
